import { Component, HostBinding, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  HATE_RATING, HateReason, LIKE_RATING, TrackRatingService
} from '../../../core/services/track-rating.service';
import { PlaybackService } from '../../../core/services/playback.service';
import { WebPlaybackTrack } from '../../models/spotify.models';

interface ReasonOption {
  value: HateReason;
  label: string;
}

/**
 * "Rate" button + popover of large one-tap choices: Like, Hate (reason
 * "just don't like"), and hate-with-a-reason. Any hate saves and skips the current track (both surfaces only
 * ever show the now-playing track). Two appearances: 'overlay' (round, sits
 * over album art) and 'button' (labeled raised button).
 */
@Component({
  selector: 'app-track-rating-button',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule],
  templateUrl: './track-rating-button.component.html',
  styleUrls: ['./track-rating-button.component.scss']
})
export class TrackRatingButtonComponent implements OnChanges {
  @Input() track: WebPlaybackTrack | null = null;
  @Input() appearance: 'overlay' | 'button' = 'button';

  @HostBinding('class.overlay-host') get isOverlay(): boolean {
    return this.appearance === 'overlay';
  }

  private trackRatingService = inject(TrackRatingService);
  private playbackService = inject(PlaybackService);
  private snackBar = inject(MatSnackBar);

  currentRating: number | null = null;
  currentReason: HateReason | null = null;

  readonly reasons: ReasonOption[] = [
    { value: 'repetitive', label: 'Repetitive' },
    { value: 'screamy', label: 'Screamy' },
    { value: 'too_clubby', label: 'Too clubby' },
    { value: 'too_whiny', label: 'Too whiny' }
  ];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['track']) {
      const previous = changes['track'].previousValue as WebPlaybackTrack | null;
      if (previous?.id !== this.track?.id) {
        this.loadRating();
      }
    }
  }

  // Sign decides like/hate, so legacy -2..+2 ratings still display correctly.
  get isLiked(): boolean {
    return this.currentRating !== null && this.currentRating > 0;
  }

  get isHated(): boolean {
    return this.currentRating !== null && this.currentRating < 0;
  }

  get buttonIcon(): string {
    if (this.isLiked) {
      return 'thumb_up';
    }
    if (this.isHated) {
      return 'thumb_down';
    }
    return 'thumbs_up_down';
  }

  like(): void {
    this.save(LIKE_RATING, null);
  }

  async hate(reason: HateReason): Promise<void> {
    if (!this.save(HATE_RATING, reason)) {
      return;
    }
    // Hate = "get this off" — skip to the next track.
    try {
      await this.playbackService.nextTrack();
    } catch (error) {
      console.error('[rating] failed to skip after hate:', error);
    }
  }

  /** Saves optimistically (the menu closes immediately). Returns false if there is no track. */
  private save(rating: number, reason: HateReason | null): boolean {
    const track = this.track;
    if (!track?.id) {
      return false;
    }

    this.currentRating = rating;
    this.currentReason = reason;
    this.trackRatingService.rateTrack({
      trackId: track.id,
      trackName: track.name,
      artistName: track.artists?.map(artist => artist.name).join(', ') || 'Unknown Artist',
      albumName: track.album?.name || undefined,
      rating,
      reason: reason ?? undefined
    }).subscribe({
      error: error => {
        console.error('[rating] failed to save rating:', error);
        this.snackBar.open('Failed to save rating', undefined, { duration: 3000 });
      }
    });
    return true;
  }

  private loadRating(): void {
    const trackId = this.track?.id;
    this.currentRating = null;
    this.currentReason = null;
    if (!trackId) {
      return;
    }
    this.trackRatingService.getRating(trackId).subscribe({
      next: rating => {
        // Only apply if the track hasn't changed while the request was in flight.
        if (this.track?.id === trackId) {
          this.currentRating = rating?.rating ?? null;
          this.currentReason = rating?.reason ?? null;
        }
      },
      error: error => console.error('[rating] failed to load rating:', error)
    });
  }
}
